export const manageDialogFetchSize = 10000;

export interface PageClientRowsOptions<TItem> {
  items: readonly TItem[];
  searchTerm: string;
  searchText: (item: TItem) => string;
  page: number;
  pageSize: number;
  matches?: ((item: TItem) => boolean) | undefined;
}

export interface PageClientRowsResult<TItem> {
  items: TItem[];
  filteredItems: TItem[];
  totalItems: number;
  page: number;
}

export interface LoadedSelectionHeaderState {
  checked: boolean | 'indeterminate';
  disabled: boolean;
}

export function searchClientRows<TItem>({
  items,
  searchTerm,
  searchText,
}: Pick<PageClientRowsOptions<TItem>, 'items' | 'searchTerm' | 'searchText'>): TItem[] {
  const needle = searchTerm.trim().toLocaleLowerCase();
  if (!needle) return [...items];
  return items.filter((item) => searchText(item).toLocaleLowerCase().includes(needle));
}

export function pageClientRows<TItem>({
  items,
  searchTerm,
  searchText,
  page,
  pageSize,
  matches,
}: PageClientRowsOptions<TItem>): PageClientRowsResult<TItem> {
  const searched = searchClientRows({ items, searchTerm, searchText });
  const filtered = matches ? searched.filter(matches) : searched;
  const lastPage = Math.max(0, Math.ceil(filtered.length / pageSize) - 1);
  const resolvedPage = Math.min(page, lastPage);
  const start = resolvedPage * pageSize;
  return {
    items: filtered.slice(start, start + pageSize),
    filteredItems: filtered,
    totalItems: filtered.length,
    page: resolvedPage,
  };
}

export function loadedSelectionHeaderState(
  selectedIds: ReadonlySet<string>,
  targetIds: readonly string[],
): LoadedSelectionHeaderState {
  if (targetIds.length === 0) {
    return { checked: false, disabled: true };
  }
  let selectedCount = 0;
  for (const id of targetIds) {
    if (selectedIds.has(id)) selectedCount += 1;
  }
  if (selectedCount === 0) {
    return { checked: false, disabled: false };
  }
  if (selectedCount === targetIds.length) {
    return { checked: true, disabled: false };
  }
  return { checked: 'indeterminate', disabled: false };
}

export function mergeLoadedSelection<TDraft, TTarget>(
  draft: readonly TDraft[],
  targets: readonly TTarget[],
  checked: boolean,
  getDraftId: (item: TDraft) => string,
  getTargetId: (item: TTarget) => string,
  toDraft: (item: TTarget) => TDraft,
): TDraft[] {
  const targetIds = new Set(targets.map(getTargetId));
  const kept = draft.filter((item) => !targetIds.has(getDraftId(item)));
  if (!checked) return kept;
  return [...kept, ...targets.map(toDraft)];
}

export function countByStatus<TStatus extends string>(
  items: readonly { status: TStatus }[],
): Partial<Record<TStatus, number>> {
  const counts: Partial<Record<TStatus, number>> = {};
  for (const item of items) {
    counts[item.status] = (counts[item.status] ?? 0) + 1;
  }
  return counts;
}
